import DataTable from 'react-data-table-component'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  debounce,
  fetchData,
  toasterrormsg,
  useMenuIdPermissions,
} from '../../../reusable'
import { KTIcon } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import { Tr } from '../../../_metronic/i18n/Tr'
import { Modal } from 'react-bootstrap'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import moment from 'moment'
import DOMPurify from 'dompurify'

const tmp = sessionStorage.getItem('admin')
const adminId = tmp ? JSON.parse(tmp)?.adminId ?? '' : ''

// Fields to hide from display (technical fields, media paths)
const HIDDEN_FIELDS = /^facilities$|^mediaPath$|^thumbPath$|^mediaType$/i

// Hide fields that are pure numeric IDs (value is just a number), but keep arrays/objects with labels
const isHiddenField = (field: any): boolean => {
  if (!field?.field) return true
  if (HIDDEN_FIELDS.test(field.field)) return true
  // Only hide Id fields if both old and new values are simple numbers/strings (not arrays/objects with labels)
  if (/Id$/.test(field.field)) {
    const isSimple = (v: any) => v === null || v === undefined || typeof v === 'number' || (typeof v === 'string' && !v.includes('{'))
    return isSimple(field.oldValue) && isSimple(field.newValue)
  }
  return false
}

const filterVisibleFields = (fields: any[]): any[] =>
  fields.filter((f: any) => !isHiddenField(f))

// Check if this log is a media log (has mediaPath)
const getMediaImage = (fields: any[], baseUrl: string): string | null => {
  const media = fields.find((f: any) => f.field === 'mediaPath' && isImagePath(f.newValue || f.oldValue))
  if (!media) return null
  const path = media.newValue || media.oldValue
  return path?.startsWith('http') ? path : `${baseUrl}${path}`
}

// ─── Single Log Entry (used inside modal for each log in a batch) ───
const LogEntry = ({ log, imageBaseUrl, t }: { log: any; imageBaseUrl: string; t: (key: string) => string }) => {
  const rawFields: any[] = Array.isArray(log.changedFields) ? log.changedFields : []
  const changedFields = filterVisibleFields(rawFields)
  const actionBadge = getActionBadge(log.action)
  const actionLower = (log.action || '').toLowerCase()
  const hasNoOldData = changedFields.every((f: any) => f.oldValue === null || f.oldValue === undefined || f.oldValue === '')
  const isCreate = actionLower.includes('create') || hasNoOldData
  const isReject = actionLower.includes('reject') || actionLower.includes('block')
  const mediaImage = getMediaImage(rawFields, imageBaseUrl)

  // For reject/block, extract reason directly
  const rejectReason = isReject
    ? rawFields.find((f: any) => (f?.field || '').toLowerCase() === 'reason')?.newValue ?? rawFields.find((f: any) => (f?.field || '').toLowerCase() === 'reason')?.oldValue ?? null
    : null

  return (
    <div className={`audit-log-header audit-log-accent-${actionBadge.color} mb-5`}>
      {/* Log info bar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between px-6 py-4">
        <div className="d-flex align-items-center gap-3">
          <span className={`badge badge-light-${actionBadge.color} fs-8 fw-bold px-3 py-2`}>{log.action || '-'}</span>
          <span className="fw-bold text-gray-900 fs-7 text-capitalize">{log.entityType || '-'}</span>
        </div>
        <span className="text-gray-500 fs-8">{log.created ? moment(log.created).format('DD MMM YYYY, hh:mm A') : '-'}</span>
      </div>

      {/* Description */}
      {log.description && (
        <div className="px-6 pb-4">
          <div className="text-gray-600 fs-7 text-break">{log.description}</div>
        </div>
      )}

      {/* Reject/Block reason */}
      {rejectReason && (
        <div className="px-6 pb-4">
          <div className="border rounded p-4 bg-light-danger">
            <div className="text-gray-500 fs-8 fw-semibold mb-1">{t('Reason')}</div>
            <div className="fw-semibold text-danger fs-7 text-break">{String(rejectReason)}</div>
          </div>
        </div>
      )}

      {/* Media image preview */}
      {mediaImage && (
        <div className="px-6 pb-4">
          <div className="audit-log-image-preview">
            <img src={mediaImage} alt="" className="rounded border" />
          </div>
        </div>
      )}

      {/* Changed Fields (non-media, non-ID, exclude reason for reject) */}
      {(() => {
        const fields = isReject ? changedFields.filter((f: any) => (f?.field || '').toLowerCase() !== 'reason') : changedFields
        return fields.length > 0 ? (
        <div className="px-6 pb-5">
          {isCreate ? (
            <div className="border rounded overflow-hidden">
              <div className="row g-0 bg-light-success px-4 py-2">
                <div className="col-md-6"><span className="fw-semibold text-success fs-8">{t('Field')}</span></div>
                <div className="col-md-6"><span className="fw-semibold text-success fs-8">{t('Value')}</span></div>
              </div>
              {changedFields.map((change: any, idx: number) => (
                <div key={change.field || idx} className="row g-0 border-top px-4 py-3 align-items-center">
                  <div className="col-md-6">
                    <span className="fw-bold text-gray-800 fs-7">{formatFieldName(change.field)}</span>
                  </div>
                  <div className="col-md-6">
                    <ChangeValue value={change.newValue} type="new" imageBaseUrl={imageBaseUrl} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            changedFields.map((change: any, idx: number) => (
              <div key={change.field || idx} className="border rounded mb-3 overflow-hidden">
                <div className="bg-light px-4 py-3">
                  <span className="fw-bold text-gray-800 fs-7">{formatFieldName(change.field)}</span>
                </div>
                <div className="row g-0">
                  <div className="col-md-6 border-end">
                    <div className="bg-light-danger px-4 py-2">
                      <span className="fw-semibold text-danger fs-8">{t('Old Value')}</span>
                    </div>
                    <div className="p-4">
                      <ChangeValue value={change.oldValue} type="old" imageBaseUrl={imageBaseUrl} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-light-success px-4 py-2">
                      <span className="fw-semibold text-success fs-8">{t('New Value')}</span>
                    </div>
                    <div className="p-4">
                      <ChangeValue value={change.newValue} type="new" imageBaseUrl={imageBaseUrl} />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null; })()}
    </div>
  )
}

// ─── Batch Detail Modal ───
const ChangeDetailModal = ({ show, onHide, batch, imageBaseUrl }: { show: boolean; onHide: () => void; batch: any; imageBaseUrl: string }) => {
  const t = Tr()
  if (!batch) return null

  const logs: any[] = Array.isArray(batch.logs) ? batch.logs : []

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable dialogClassName="mw-1000px">
      <Modal.Header closeButton className="border-0 pb-0 px-8 pt-8">
        <Modal.Title className="fs-3 fw-bold">{t('Audit Log Details')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-8 pt-5">

        {/* Batch Header - Admin + Reference */}
        <div className="audit-log-header audit-log-accent-primary mb-7">
          <div className="d-flex align-items-center justify-content-between px-7 py-5">
            <div className="d-flex align-items-center gap-4 min-w-0">
              <div className="symbol symbol-50px symbol-circle flex-shrink-0">
                {batch.adminProfilePicture ? (
                  <img src={batch.adminProfilePicture} alt="" className="symbol-label" />
                ) : (
                  <span className="symbol-label bg-light-primary text-primary fw-bold fs-3">
                    {(batch.adminName || 'A').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                  <span className="fw-bolder text-gray-900 fs-5">{batch.adminName || '-'}</span>
                  <span className="badge badge-light-info fs-9 flex-shrink-0">{batch.adminRoleName || ''}</span>
                </div>
                <div className="text-gray-500 fs-7 text-truncate">
                  {batch.adminEmail || ''}
                  {batch.adminEmail && batch.adminMobileNumber ? ' \u00B7 ' : ''}
                  {batch.adminMobileNumber ? `+${batch.adminCountryCode || ''} ${batch.adminMobileNumber}` : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Reference details */}
          <div className="audit-log-details mx-7 mb-5">
            <div className="row g-0">
              <div className="col-4 px-5 py-4 audit-log-detail-cell">
                <div className="text-gray-500 fs-8 fw-semibold mb-1">{t('Module')}</div>
                <div className="fw-bold text-gray-900 fs-6 text-capitalize">{batch.moduleName || '-'}</div>
              </div>
              <div className="col-4 px-5 py-4 audit-log-detail-cell">
                <div className="text-gray-500 fs-8 fw-semibold mb-1">{t('Reference')}</div>
                <div className="fw-bold text-gray-900 fs-6">{batch.referenceTitle || '-'}</div>
              </div>
              <div className="col-4 px-5 py-4">
                <div className="text-gray-500 fs-8 fw-semibold mb-1">{t('Date')}</div>
                <div className="fw-bold text-gray-900 fs-6">{batch.date ? moment(batch.date).format('DD MMM YYYY') : '-'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Log Entries */}
        {logs.length === 0 ? (
          <div className="text-center py-10">
            <div className="text-gray-400 fs-5">{t('No changes recorded')}</div>
          </div>
        ) : (
          <>
            <div className="fw-bold text-gray-900 fs-6 mb-4">
              {t('Changes')} <span className="text-gray-500 fs-7 fw-semibold">({logs.length})</span>
            </div>
            {logs.map((logItem: any, idx: number) => (
              <LogEntry key={logItem.adminauditlogsId || idx} log={logItem} imageBaseUrl={imageBaseUrl} t={t} />
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 px-6 pb-6 pt-2">
        <button className="btn btn-light-primary" onClick={onHide}>{t('Close')}</button>
      </Modal.Footer>
    </Modal>
  )
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i

const isImagePath = (val: any): boolean => {
  if (typeof val !== 'string') return false
  return IMAGE_EXTENSIONS.test(val)
}

const ChangeValue = ({ value, type, imageBaseUrl = '' }: { value: any; type: 'old' | 'new'; imageBaseUrl?: string }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 fst-italic fs-8">-</span>
  }

  const colorClass = type === 'old' ? 'danger' : 'success'

  // Image path detection
  if (isImagePath(value)) {
    const src = value.startsWith('http') ? value : `${imageBaseUrl}${value}`
    return (
      <div className="audit-log-image-preview">
        <img src={src} alt="" className="rounded border mw-100" />
      </div>
    )
  }

  // Array of {value, label} objects
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400 fst-italic fs-8">-</span>
    if (value[0] && typeof value[0] === 'object' && 'label' in value[0]) {
      return (
        <ul className="list-unstyled mb-0">
          {value.map((item: any, i: number) => (
            <li key={i} className="d-flex align-items-center gap-2 py-1">
              <span className={`bullet bullet-dot bg-${colorClass} h-6px w-6px`}></span>
              <span className="text-gray-800 fs-7">{item.label}</span>
            </li>
          ))}
        </ul>
      )
    }
    return (
      <ul className="list-unstyled mb-0">
        {value.map((item: any, i: number) => (
          <li key={i} className="d-flex align-items-center gap-2 py-1">
            <span className={`bullet bullet-dot bg-${colorClass} h-6px w-6px`}></span>
            <span className="text-gray-800 fs-7">{String(item)}</span>
          </li>
        ))}
      </ul>
    )
  }

  // Single {value, label} object
  if (typeof value === 'object' && 'label' in value) {
    return <span className="fw-semibold text-gray-800 fs-7">{value.label}</span>
  }

  // Plain object (key-value pairs)
  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value)
    if (entries.length === 0) return <span className="text-gray-400 fst-italic fs-8">-</span>
    return (
      <ul className="list-unstyled mb-0">
        {entries.map(([key, val]) => (
          <li key={key} className="d-flex align-items-center gap-2 py-1">
            <span className="fw-semibold text-gray-800 fs-7">{formatFieldName(key)}:</span>
            <span className={`fs-7 ${val === true ? 'text-success' : val === false ? 'text-danger' : 'text-gray-700'}`}>
              {val === true ? 'Yes' : val === false ? 'No' : String(val ?? '-')}
            </span>
          </li>
        ))}
      </ul>
    )
  }

  // Plain value
  const strVal = String(value)

  // Status badge detection
  const statusMap: Record<string, string> = {
    'suspended': 'danger',
    'suspend': 'danger',
    'blocked': 'danger',
    'rejected': 'danger',
    'inactive': 'danger',
    'disabled': 'danger',
    'activated': 'success',
    'active': 'success',
    'approved': 'success',
    'enabled': 'success',
    'verified': 'success',
    'pending': 'warning',
    'featured': 'info',
    'unfeatured': 'warning',
    'reported': 'danger',
  }
  const statusColor = statusMap[strVal.toLowerCase()]
  if (statusColor) {
    return <span className={`badge badge-light-${statusColor} fw-bold fs-7 px-3 py-2`}>{strVal}</span>
  }

  // Detect JSON string — parse and render nicely
  if (strVal.startsWith('[') || strVal.startsWith('{')) {
    try {
      const parsed = JSON.parse(strVal)
      if (Array.isArray(parsed)) {
        // Date/availability arrays — compact rendering
        const isDateArray = parsed.length > 0 && parsed.every((item: any) => item && typeof item === 'object' && ('start' in item || 'end' in item))
        if (isDateArray) {
          return (
            <ul className="list-unstyled mb-0">
              {parsed.map((item: any, i: number) => {
                const startDate = item.start ? moment(item.start).format('DD MMM YYYY') : ''
                const endDate = item.end ? moment(item.end).format('DD MMM YYYY') : ''
                const dateStr = startDate && endDate && startDate !== endDate
                  ? `${startDate} — ${endDate}`
                  : startDate || endDate || '-'
                const title = item.title || item.type || ''
                return (
                  <li key={i} className="d-flex align-items-center gap-2 py-1">
                    <span className={`bullet bullet-dot bg-${colorClass} h-6px w-6px flex-shrink-0`}></span>
                    <span className="text-gray-800 fs-7">{dateStr}{title ? ` · ${title}` : ''}</span>
                  </li>
                )
              })}
            </ul>
          )
        }

        return (
          <div className="d-flex flex-column gap-2">
            {parsed.map((item: any, i: number) => {
              if (typeof item === 'object' && item !== null) {
                const entries = Object.entries(item)
                return (
                  <div key={i} className="border rounded px-3 py-3">
                    {entries.map(([key, val]) => (
                      <div key={key} className="py-1">
                        <span className="text-gray-500 fs-7 fw-semibold">{formatFieldName(key)}</span>
                        <div className="fw-semibold text-gray-800 fs-7 text-break">{String(val ?? '-')}</div>
                      </div>
                    ))}
                  </div>
                )
              }
              return <div key={i} className="text-gray-800 fs-7">{String(item)}</div>
            })}
          </div>
        )
      }
      if (typeof parsed === 'object' && parsed !== null) {
        return (
          <div className="border rounded px-3 py-3">
            {Object.entries(parsed).map(([key, val]) => (
              <div key={key} className="py-1">
                <span className="text-gray-500 fs-7 fw-semibold">{formatFieldName(key)}</span>
                <div className="fw-semibold text-gray-800 fs-7 text-break">{String(val ?? '-')}</div>
              </div>
            ))}
          </div>
        )
      }
    } catch {
      // Not valid JSON, fall through to plain text
    }
  }

  // Render HTML content safely if it contains tags
  if (/<[a-z][\s\S]*>/i.test(strVal)) {
    return (
      <div className="audit-log-html-scroll">
        <div
          className="fw-semibold text-gray-800 fs-7 audit-log-html-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(strVal, { ALLOW_DATA_ATTR: false, ADD_ATTR: ['style', 'class', 'align', 'dir'] }) }}
        />
      </div>
    )
  }

  return (
    <div className="fw-semibold text-gray-800 fs-7 text-break">{strVal}</div>
  )
}

// ─── Helpers ───
function formatFieldName(key: string): string {
  if (!key) return '-'
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/Id$/, '')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim()
}

function getActionBadge(action: string): { color: string; icon: string } {
  const lower = (action || '').toLowerCase()
  if (lower.includes('delete') || lower.includes('remove')) return { color: 'danger', icon: 'trash' }
  if (lower.includes('add') || lower.includes('create') || lower.includes('insert')) return { color: 'success', icon: 'plus' }
  if (lower.includes('edit') || lower.includes('update') || lower.includes('change')) return { color: 'warning', icon: 'pencil' }
  if (lower.includes('feature') && lower.includes('un')) return { color: 'warning', icon: 'minus' }
  if (lower.includes('feature')) return { color: 'info', icon: 'star' }
  if (lower.includes('approve') || lower.includes('accept')) return { color: 'primary', icon: 'check' }
  if (lower.includes('reject') || lower.includes('block') || lower.includes('suspend') || lower.includes('report')) return { color: 'danger', icon: 'cross' }
  if (lower.includes('activate') || lower.includes('restore')) return { color: 'success', icon: 'check' }
  if (lower.includes('login') || lower.includes('auth')) return { color: 'info', icon: 'lock' }
  return { color: 'dark', icon: 'abstract-26' }
}

// ─── Custom Header (search + date filter) ───
const CustomHeader = React.memo(({
  search,
  dateRange,
  onSearch,
  onDateChange,
  onClear,
  onReload,
  isReloading,
  searchDisabled,
  t,
}: {
  search: string
  dateRange: Date[]
  onSearch: (text: string) => void
  onDateChange: (dates: Date[]) => void
  onClear: () => void
  onReload: () => void
  isReloading: boolean
  searchDisabled: boolean
  t: (key: string) => string
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const flatpickrRef = useRef<any>(null)
  const [showDone, setShowDone] = useState(false)
  const prevReloading = useRef(isReloading)

  useEffect(() => {
    if (prevReloading.current && !isReloading) {
      setShowDone(true)
      const timer = setTimeout(() => setShowDone(false), 1500)
      return () => clearTimeout(timer)
    }
    prevReloading.current = isReloading
  }, [isReloading])
  const flatpickrOptions = useMemo(() => ({
    mode: 'range' as const,
    dateFormat: 'd M Y',
  }), [])

  useEffect(() => {
    if (dateRange.length === 0 && flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.clear()
    }
  }, [dateRange])

  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 w-100 my-5">
      <div className="d-flex align-items-center position-relative">
        <KTIcon iconName="magnifier" className="fs-1 position-absolute ms-6" />
        <input
          type="text"
          className="form-control form-control-solid w-250px ps-14"
          placeholder={t('Search Activity Logs')}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          ref={inputRef}
          disabled={searchDisabled}
        />
      </div>
      <div className="d-flex align-items-center gap-3">
        <div className="d-flex align-items-center position-relative">
          <KTIcon iconName="calendar" className="fs-3 position-absolute ms-4 text-gray-500" />
          <Flatpickr
            ref={flatpickrRef}
            className="form-control form-control-solid w-275px ps-12"
            placeholder={t('Filter by date range')}
            value={dateRange}
            options={flatpickrOptions}
            onClose={(dates: Date[]) => onDateChange(dates)}
          />
        </div>
        <button
          className={`btn btn-sm btn-icon ${showDone ? 'btn-light-success' : 'btn-light-primary'}`}
          onClick={onReload}
          disabled={isReloading}
          title={t('Reload')}
        >
          {isReloading ? (
            <span className="spinner-border spinner-border-sm" role="status"></span>
          ) : showDone ? (
            <KTIcon iconName="check" className="fs-3 text-success" />
          ) : (
            <KTIcon iconName="arrows-circle" iconType='solid' className="fs-3" />
          )}
        </button>
        {(dateRange.length > 0 || search) && (
          <button className="btn btn-sm btn-light-danger" onClick={onClear} title={t('Clear Filters')}>
            <KTIcon iconName="cross" className="fs-5 me-1" />
            {t('Clear')}
          </button>
        )}
      </div>
    </div>
  )
})

// ─── Main Component ───
const ViewActivityLog: FC = () => {
  const t = Tr()
  const { menuId } = useMenuIdPermissions()

  const [isLoading, setIsLoading] = useState(false)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [logList, setLogList] = useState<any[]>([])
  const [inputValue, setInputValue] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLog, setSelectedLog] = useState<any>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [imageBaseUrl, setImageBaseUrl] = useState('')
  

  // Pagination & Sort (server-side)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [sortField, setSortField] = useState('')
  const [sortOrder, setSortOrder] = useState('')

  // Date filter
  const [dateRange, setDateRange] = useState<Date[]>([])

  const activityLogApiCall = async () => {
    try {
      setIsLoading(true)
      const param: any = {
        // adminId: adminId,
        limit: limit,
        offset: offset,
        search: searchTerm.trim(),
        sortBy: sortField,
        sort: sortOrder,
      }
      if (dateRange.length === 2) {
        param.fromDate = moment(dateRange[0]).format('YYYY-MM-DD')
        param.toDate = moment(dateRange[1]).format('YYYY-MM-DD')
      }

      const res = await fetchData('admin/adminActivityLogList', param)
      if (res.status == '200') {
        setLogList(res.data?.list || [])
        setTotalRecords(Number(res.data?.totalRecords) || 0)
        if (res?.url) setImageBaseUrl(res.url)
        setIsDataLoaded(true)
      } else {
        toasterrormsg(res.message)
        setLogList([])
        setTotalRecords(0)
      }
    } catch (error) {
      toasterrormsg('Failed to fetch activity logs')
      setLogList([])
      setTotalRecords(0)
    } finally {
      setIsLoading(false)
      setIsDataLoaded(true)
    }
  }

  // Server-side search with debounce
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchTerm(value)
      setOffset(0)
    }, 500)
  ).current

  const handleSearch = useCallback((text: string) => {
    setInputValue(text)
    debouncedSearch(text)
  }, [debouncedSearch])

  const handleDateChange = useCallback((dates: Date[]) => {
    if (dates.length === 2) {
      setDateRange(dates)
      setOffset(0)
    }
  }, [])

  useEffect(() => {
    document.title="Insayr | Activity Logs"
    return () => debouncedSearch.cancel()
  }, [])

  // Server-side sort
  const handleSort = (column: any, sortDirection: string) => {
    const fieldName = column.sortField || ''
    setSortField(fieldName)
    setSortOrder(sortDirection === 'asc' ? 'asc' : 'desc')
  }

  // Server-side pagination
  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) {
      setOffset(0)
    } else if (newPage === totalPages) {
      setOffset((totalPages - 1) * limit)
    } else {
      setOffset((newPage - 1) * limit)
    }
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }

  const handleClearFilters = () => {
    setDateRange([])
    setInputValue('')
    setSearchTerm('')
    setOffset(0)
  }

  // Re-fetch when any server-side param changes
  useEffect(() => {
    activityLogApiCall()
  }, [limit, offset, sortField, sortOrder, searchTerm, dateRange])


  // ─── Columns (batch-level) ───
  const columns: any = useMemo(
    () => [
      {
        name: t('Admin'),
        cell: (row: any) => (
          <div className="d-flex align-items-center gap-3 py-3">
            <div className="symbol symbol-40px symbol-circle">
              {row.adminProfilePicture ? (
                <img src={row.adminProfilePicture} alt="" className="symbol-label" />
              ) : (
                <span className="symbol-label bg-light-primary text-primary fw-bold fs-6">
                  {(row.adminName || 'A').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <div className="fw-bold text-gray-900 fs-6">{row.adminName || '-'}</div>
              <div className="text-gray-500 fs-8">{row.adminRoleName || ''}</div>
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'adminName',
        selector: (row: any) => row.adminName || '',
        width: '200px',
      },
      {
        name: t('Description'),
        cell: (row: any) => {
          const logs: any[] = Array.isArray(row.logs) ? row.logs : []
          const firstLog = logs[0] || {}
          const desc = firstLog.description || '-'
          return (
            <div className="py-3">
              <div className="text-gray-900 fs-6 fw-semibold text-break">{desc}</div>
              {logs.length > 1 && <div className="text-gray-500 fs-8 mt-1">+{logs.length - 1} {t('more')}</div>}
            </div>
          )
        },
        sortable: false,
                        selector: (row: any) => row.description || "",

        grow: 2,
      },
      {
        name: t('Module'),
        cell: (row: any) => (
          <span className="fw-semibold text-gray-800 fs-6 text-capitalize">{row.moduleName || '-'}</span>
        ),
        sortable: true,
        sortField: 'moduleName',
                                selector: (row: any) => row.moduleName || "",

        width: '160px',
      },
      {
        name: t('Action'),
        cell: (row: any) => {
          const logs: any[] = Array.isArray(row.logs) ? row.logs : []
          const actions = [...new Set(logs.map((l: any) => l.action).filter(Boolean))]
          return (
            <div className="d-flex flex-wrap gap-2">
              {actions.map((action: string, i: number) => {
                const badge = getActionBadge(action)
                return <span key={i} className={`badge badge-light-${badge.color} px-3 py-2 fs-8 fw-bold`}>{action}</span>
              })}
            </div>
          )
        },
        width: '140px',
      },
      {
        name: t('Date & Time'),
        cell: (row: any) => {
          const logs: any[] = Array.isArray(row.logs) ? row.logs : []
          const created = logs[0]?.created || row.date
          if (!created) return <span className="text-gray-400 fs-7">-</span>
          return (
            <div>
              <div className="fw-bold text-gray-900 fs-6">{moment(created).format('DD MMM YYYY')}</div>
              <div className="text-gray-500 fs-8">{moment(created).format('hh:mm A')}</div>
            </div>
          )
        },
        sortable: true,
        sortField: 'date',
        selector: (row: any) => row.date || '',
        width: '150px',
      },
      {
        name: t('Details'),
        cell: (row: any) => (
          <button
            className="btn btn-sm btn-light-primary px-4 py-2"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedLog(row)
              setShowDetail(true)
            }}
          >
            <KTIcon iconName="eye" className="fs-5 me-1" />
            {t('View')}
          </button>
        ),
        width: '120px',
      },
    ],
    [t]
  )

  return (
    <>
      <ToastContainer />
        <div className="card">
          <div className="card-body py-4">
            <DataTable
              columns={columns}
              data={logList}
              pagination
              paginationServer
              paginationTotalRows={totalRecords}
              paginationPerPage={limit}
              paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
              sortServer
              onSort={handleSort}
              progressPending={isLoading}
              progressComponent={
                <div className="py-20 text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('Loading...')}</span>
                  </div>
                </div>
              }
              noDataComponent={
                <div className="text-center py-20">
                  <KTIcon iconName="document" className="fs-2tx text-gray-300 mb-5" />
                  <div className="text-gray-500 fs-5">{t('No activity logs found')}</div>
                </div>
              }
              subHeader
              subHeaderComponent={
                <CustomHeader
                  search={inputValue}
                  dateRange={dateRange}
                  onSearch={handleSearch}
                  onDateChange={handleDateChange}
                  onClear={handleClearFilters}
                  onReload={activityLogApiCall}
                  isReloading={isLoading}
                  searchDisabled={dateRange.length === 2 && logList.length === 0 && !inputValue}
                  t={t}
                />
              }
              paginationComponentOptions={{
                rowsPerPageText: t('Rows Per Page'),
              }}
              highlightOnHover
            />
          </div>
        </div>

      <ChangeDetailModal
        show={showDetail}
        onHide={() => { setShowDetail(false); setSelectedLog(null) }}
        batch={selectedLog}
        imageBaseUrl={imageBaseUrl}
      />
    </>
  )
}

export default ViewActivityLog
